import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { RpcService } from '../http/rpc.service';
import { combineLatest, Observable, Subject } from 'rxjs';
import { CompanySettingsDto } from 'app/settings/dto/company-settings.dto';
import { AccountDTO } from 'app/account/dto/account.dto';
import { map, filter, takeUntil, debounceTime } from 'rxjs/operators';
import { UserDTO } from 'app/user-management/users/user';
import { environment } from 'environments/environment';

declare const initIntercom;
declare const Intercom;

@Injectable({
  providedIn: 'root'
})
export class IntercomService {
  private intercomKey: string;
  private shutdownSubject = new Subject<boolean>();

  constructor(private rpc: RpcService, private router: Router) {
    this.intercomKey = environment.intercom;
    if (this.intercomKey) {
      initIntercom(this.intercomKey);
    }
  }

  private getAccount(
    token: string
  ): Observable<AccountDTO & { usertype: string }> {
    return this.rpc.makeManagerCall('getAccount', [], token);
  }

  private getCompanySettings(token: string): Observable<CompanySettingsDto> {
    return this.rpc.makeManagerCall('getCompany', [], token);
  }

  private getUser(token: string): Observable<UserDTO & { uuid: string }> {
    return this.rpc.makeManagerCall('getUser', [], token);
  }

  private getSubscriptionPlan(numberOfDevices: number) {
    if (numberOfDevices <= 1) {
      return 'small';
    }
    if (numberOfDevices <= 3) {
      return 'medium';
    }
    return 'large';
  }

  private getPaymentTypes(token: string): Observable<{ typeId: number }[]> {
    return this.rpc.makeManagerCall('getPaymentTypes', [], token);
  }

  private isPaymentTypeConfigured(types: { typeId: number }[], typeId: number) {
    return types.some(paymentType => paymentType.typeId === typeId);
  }

  private getAllCompanySettings(
    token: string,
    apiToken: string
  ): Observable<
    CompanySettingsDto & { customer: { name: string; id: number } }
  > {
    if (token === apiToken) {
      return this.getCompanySettings(token).pipe(
        map(settings => ({
          ...settings,
          customer: { name: settings.name, id: settings.masterCompanyId }
        }))
      );
    }
    return combineLatest(
      this.getCompanySettings(token),
      this.getCompanySettings(apiToken)
    ).pipe(
      map(([settings, customerSettings]) => ({
        ...settings,
        customer: {
          name: customerSettings.name,
          id: customerSettings.masterCompanyId
        }
      }))
    );
  }

  public init() {
    if (this.intercomKey) {
      const token =
        sessionStorage.getItem('partnerapitoken') ||
        sessionStorage.getItem('apitoken');
      const source$ = combineLatest(
        this.getAccount(token),
        this.getAllCompanySettings(token, sessionStorage.getItem('apitoken')),
        this.getUser(token),
        this.getPaymentTypes(token)
      ).pipe(
        map(([account, settings, user, paymentTypes]) => {
          const customCompanyProperties =
            account && account.subscriptionNumber
              ? {
                  unique_subscription_id: account.subscriptionNumber
                }
              : {};
          return {
            name: user.username,
            email: user.email,
            user_id: `${settings.masterCompanyId}-${user.oid}`,
            phone: settings.telephone,
            location: settings.country,
            company: {
              ...customCompanyProperties,
              name: settings.name,
              company_id: settings.masterCompanyId,
              created_at:
                account && account.creationDate
                  ? new Date(account.creationDate).getTime() / 1000
                  : null,
              plan: this.getSubscriptionPlan(account.allowedNumberOfDevices),
              website: settings.url
            },
            server: location.hostname.replace('-web', '').split('.')[0],
            allowed_number_of_devices: account.allowedNumberOfDevices,
            account_expiration:
              account && account.expirationDate
                ? new Date(account.expirationDate).toISOString()
                : null,
            account_subscription_status: account.type,
            account_user_type: account.usertype || 'regular',
            user_type: user.type,
            enabled_kitchen_display_system: settings.kdsEnabled,
            enabled_advanded_reporting: settings.enableAdvancedReporting,
            enabled_customer_facing_display: settings.cfdEnabled,
            enabled_self_order_menu: settings.menuEnabled,
            enabled_lightspeed_loyalty: this.isPaymentTypeConfigured(
              paymentTypes,
              2700
            ),
            enabled_lightspeed_payments: this.isPaymentTypeConfigured(
              paymentTypes,
              2900
            ),
            referral_name: settings.customer.name,
            referral_id: settings.customer.id
          };
        })
      );
      source$.subscribe(({ email, user_id, ...intercomObject }) => {
        Intercom('boot', { app_id: this.intercomKey, email, user_id });
        Intercom('update', intercomObject);
      });

      this.router.events
        .pipe(
          filter(event => event instanceof NavigationEnd),
          debounceTime(30000),
          takeUntil(this.shutdownSubject)
        )
        .subscribe(() => {
          Intercom('update');
        });
    }
  }
  public logOut() {
    if (this.intercomKey) {
      this.shutdownSubject.next(true);
      Intercom('shutdown');
    }
  }

  public open() {
    if (this.intercomKey) {
      Intercom('show');
    }
  }

  public trackEvent(eventName: string, metadata?: object): void {
    if (this.intercomKey) {
      Intercom('trackEvent', eventName, metadata);
    }
  }

  public toggle() {
    const intercomHidden = window.sessionStorage.getItem('intercomHidden');

    if (intercomHidden) {
      window.sessionStorage.setItem(
        'intercomHidden',
        intercomHidden === 'true' ? 'false' : 'true'
      );
      Intercom('update', {
        hide_default_launcher: intercomHidden === 'true' ? false : true
      });
    } else {
      window.sessionStorage.setItem('intercomHidden', 'true');
      Intercom('update', { hide_default_launcher: true });
    }
  }
}
